<template>
  <div class="results-table-container" data-testid="reverse-ip-results-container">
    <div class="table-header">
      <div class="results-count" data-testid="reverse-ip-results-count">
        <strong>{{ domains.length }}</strong> domain(s) found
        <span v-if="ipAddress" class="ip-address" data-testid="reverse-ip-address"> for {{ ipAddress }}</span>
        <span v-if="queryTime" class="query-time" data-testid="reverse-ip-query-time"> ({{ queryTime }}s)</span>
      </div>
      <div v-if="metadata" class="metadata-info" data-testid="reverse-ip-metadata">
        <a-tag v-if="metadata.country" color="blue" data-testid="reverse-ip-country">{{ metadata.country }}</a-tag>
        <a-tag v-if="metadata.hosting_provider" color="green" data-testid="reverse-ip-provider">{{ metadata.hosting_provider }}</a-tag>
      </div>
    </div>

    <a-table
      :columns="columns"
      :data-source="sortedDomains"
      :pagination="pagination"
      :loading="loading"
      row-key="domain"
      class="results-table"
      @change="handleTableChange"
      data-testid="reverse-ip-data-table"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'domain'">
          <span class="domain-name">{{ record.domain }}</span>
        </template>
        <template v-else-if="column.key === 'source'">
          <a-tag :color="getSourceColor(record.source)">
            {{ record.source.toUpperCase() }}
          </a-tag>
        </template>
        <template v-else-if="column.key === 'status'">
          <a-badge :status="record.status === 'active' ? 'success' : 'default'" :text="record.status" />
        </template>
        <template v-else-if="column.key === 'ssl'">
          <a-tag :color="record.ssl ? 'green' : 'default'">
            {{ record.ssl ? 'Yes' : 'No' }}
          </a-tag>
        </template>
        <template v-else-if="column.key === 'last_seen'">
          {{ record.last_seen || 'N/A' }}
        </template>
      </template>
    </a-table>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { Domain, IpMetadata, ColumnKey, SortDirection } from '../types';
import type { TableColumnType, TableProps } from 'ant-design-vue';

interface Props {
  domains: Domain[];
  ipAddress?: string;
  queryTime?: number;
  metadata?: IpMetadata;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  domains: () => [],
  loading: false,
});

const sortColumn = ref<ColumnKey | null>(null);
const sortDirection = ref<SortDirection>('asc');

const columns: TableColumnType[] = [
  {
    title: 'Domain',
    dataIndex: 'domain',
    key: 'domain',
    sorter: true,
    width: '40%',
  },
  {
    title: 'Source',
    dataIndex: 'source',
    key: 'source',
    sorter: true,
    width: '15%',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    sorter: true,
    width: '15%',
  },
  {
    title: 'SSL',
    dataIndex: 'ssl',
    key: 'ssl',
    sorter: true,
    width: '10%',
  },
  {
    title: 'Last Seen',
    dataIndex: 'last_seen',
    key: 'last_seen',
    sorter: true,
    width: '20%',
  },
];

const pagination = {
  pageSize: 50,
  showSizeChanger: true,
  showTotal: (total: number) => `Total ${total} domains`,
  pageSizeOptions: ['25', '50', '100', '200'],
};

const sortedDomains = computed(() => {
  if (!sortColumn.value) {
    return props.domains;
  }

  return [...props.domains].sort((a, b) => {
    const aVal = a[sortColumn.value as ColumnKey];
    const bVal = b[sortColumn.value as ColumnKey];

    // Handle null values
    if (aVal === null && bVal === null) return 0;
    if (aVal === null) return 1;
    if (bVal === null) return -1;

    let comparison = 0;
    if (aVal < bVal) comparison = -1;
    if (aVal > bVal) comparison = 1;

    return sortDirection.value === 'asc' ? comparison : -comparison;
  });
});

const handleTableChange: TableProps['onChange'] = (pagination, filters, sorter: any) => {
  if (sorter && sorter.column) {
    sortColumn.value = sorter.columnKey as ColumnKey;
    sortDirection.value = sorter.order === 'ascend' ? 'asc' : 'desc';
  } else {
    sortColumn.value = null;
  }
};

const getSourceColor = (source: string) => {
  const colors: Record<string, string> = {
    ptr: 'blue',
    ssl: 'green',
    unknown: 'default',
  };
  return colors[source] || 'default';
};
</script>

<style scoped lang="scss">
.results-table-container {
  max-width: 1200px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 16px 24px;
  background: #f5f5f5;
  border-radius: 4px;
}

.results-count {
  font-size: 16px;
  font-weight: 600;

  strong {
    font-size: 20px;
    color: #1890ff;
  }

  .ip-address {
    color: #595959;
    font-weight: 500;
  }

  .query-time {
    color: #8c8c8c;
    font-size: 14px;
  }
}

.metadata-info {
  display: flex;
  gap: 8px;
}

.results-table {
  background: white;
  border-radius: 4px;

  :deep(th) {
    font-weight: 700;
    background: #fafafa;
  }

  :deep(td) {
    padding: 16px 24px;
  }
}

.domain-name {
  font-family: 'Courier New', Courier, monospace;
  font-weight: 600;
  color: #1890ff;
}
</style>
